import { normalizeAssistantText } from '@/lib/assistant/assistantTextUtils';

export function isFarewellMessage(n, trimmed) {
  if (trimmed.length > 52) return false;
  if (!/\b(gracias|chau|chao|hasta\s+luego|hasta\s+pronto|nos\s+vemos|bye)\b/i.test(n)) {
    return false;
  }
  if (
    /\b(tienen|tienes|tenemos|hay|precio|menu|menú|delivery|retiro|horario|pollo|carne|cuanto|cuánto|barato|promo)\b/.test(
      n
    )
  ) {
    return false;
  }
  return true;
}

export function extractGreetingAndRemainder(trimmed) {
  let rest = trimmed.trim();
  let hadGreeting = false;

  const tryStrip = (re) => {
    const m = rest.match(re);
    if (m && m.index === 0) {
      hadGreeting = true;
      rest = rest.slice(m[0].length).trim();
      rest = rest.replace(/^[,;.\-–—:]+/g, '').trim();
      return true;
    }
    return false;
  };

  let progress = true;
  while (progress) {
    progress = false;
    if (tryStrip(/^(hola|holaa+|hol[íi]|holis|holi|hey)\b[!?.,\s:]*/i)) progress = true;
    else if (tryStrip(/^(buenos\s+d[ií]as|buenas\s+tardes|buenas\s+noches)\b[!?.,\s:]*/i)) progress = true;
    else if (tryStrip(/^buenas\b[!?.,\s:]*/i)) progress = true;
    else if (tryStrip(/^(qu[eé]|q)\s+tal\b[!?.,\s:]*/i)) progress = true;
    else if (tryStrip(/^buen[oa]s\b[!?.,\s:]*/i)) progress = true;
  }

  if (/\btanto\s+tiempo\b/i.test(rest)) {
    hadGreeting = true;
    rest = rest.replace(/\btanto\s+tiempo\b[!?.,]*/gi, '').trim();
  } else if (/\btanto\s+tiempo\b/i.test(trimmed)) {
    hadGreeting = true;
  }

  rest = rest.replace(/^(y\s+|bueno[,]?\s*|ok[,]?\s*)/i, '').trim();
  return { hadGreeting, remainder: rest };
}

export function isPureHowAreYou(n, trimmed) {
  if (!/\b(como|cómo)\s+(estas|estás|estan|están)\b/.test(n)) return false;
  const r = trimmed
    .replace(/\b(como|cómo)\s+(estas|estás|estan|están)\b/gi, '')
    .replace(/[?!.,¿¡\s]+/g, '')
    .trim();
  return r.length < 2;
}

export function shortGreetingLine(n) {
  if (/\b(qu[eé]|q)\s+tal\b/.test(n) || /^buenas\b/.test(normalizeAssistantText(n))) {
    return '¡Qué tal! 👋';
  }
  return '¡Hola! 👋';
}

/** 0 = ok, 1 = molestia, 2 = fuerte / insultos (priorizar contención + WhatsApp). */
export function detectFrustrationLevel(n) {
  const strong =
    /\b(weon|weón|w[eé]on|conchetumare|conchatumare|culiao|culia\b|culiado|culi[aá]|mierd(a|ita)|estafa|ladrones|ladron|inutil|inútil|robo|imbecil|imbécil|idiota|hdp|chupapicos|\bputa\b|toaso|toa[so]s?|a\s+toaso)\b/.test(
      n
    );
  if (strong) return 2;
  const mild =
    /\b(hue+vad|huea|chucha\b|p[eé]simo|terrible|reclam|enojad|furios|indignad|nunca\s+lleg|demoran|demora|mal\s+servicio|tan\s+mala|mas\s+mala|m[aá]s\s+mala|una\s+porquer[ií]a|pesimo\s+local|mas\s+mala\s+la|mas\s+mala\s+atencion|mala\s+atencion|mala\s+atención)\b/.test(
      n
    );
  if (mild) return 1;
  return 0;
}

/** Reclamo por experiencia (no solo insulto suelto). */
function detectComplaintStory(n) {
  return /\b(ultima\s+vez|última\s+vez|la\s+ultima|lleg[oó]\s+mal|lleg[oó]\s+todo|todo\s+mal|todo\s+super\s+mal|super\s+mal|súper\s+mal|demoraron|se\s+demor|demora|nunca\s+lleg|mal\s+pedido|que\s+se\s+creen|solucion|soluci[oó]n|por\s+ac[aá])\b/.test(
    n
  );
}

/** Nivel unificado reclamo + enojo para respuesta conversacional (0–2). */
export function conversationalComplaintLevel(n) {
  const ins = detectFrustrationLevel(n);
  const story = detectComplaintStory(n);
  if (ins >= 2) return 2;
  if (ins >= 1 && story) return 2;
  if (ins >= 1 || story) return 1;
  return 0;
}

/** Ansiedad por plazo / cumplimiento (no buscar productos). */
export function detectDeliveryAnxiety(n) {
  if (conversationalComplaintLevel(n) >= 1) return false;
  return /\b(a\s+tiempo|a\s+la\s+hora|no\s+se\s+demoren|ojal[aá]|espero\s+que\s+(no\s+)?demoren|llegue\s+r[aá]pido|llegue\s+pronto|quiero\s+que\s+llegue|necesito\s+que\s+llegue|r[aá]pido|rapido|a\s+tiempo\s+el\s+pedido)\b/.test(
    n
  );
}

export function complaintFallbackKeys(business, level) {
  const wa = business?.whatsapp;
  if (level >= 2 && wa) return ['whatsapp', 'order_hint', 'catalog'];
  if (wa) return ['order_hint', 'whatsapp', 'catalog'];
  return ['order_hint', 'catalog', 'main'];
}

function complaintBodyText(level, withGreetingPrefix) {
  const soft = 'Pucha, lamento que te haya pasado eso 😕 Quiero ayudarte a que esta vez sea mejor.';
  const firmNoDup =
    'Quiero resolverlo lo antes posible. Si preferís hablar directo con el local, también podés escribirles por WhatsApp.';
  if (withGreetingPrefix) {
    return level >= 2
      ? `Pucha, lamento que te haya pasado eso. ${firmNoDup}`
      : soft;
  }
  if (level >= 2) {
    return `Entiendo tu molestia 🙌 ${firmNoDup}`;
  }
  return soft;
}

/**
 * Mensajes “humanos” sin saludo previo: reclamo o ansiedad de entrega.
 */
export function tryConversationalStandalone(menuData, trimmed, n, business, defaultFallback, servicePreference = null) {
  const { hadGreeting } = extractGreetingAndRemainder(trimmed);
  if (hadGreeting) return null;

  if (detectDeliveryAnxiety(n)) {
    const text =
      servicePreference === 'pickup'
        ? 'Perfecto 👌 Al armar el pedido para retiro vas a ver el tiempo estimado antes de confirmar.'
        : 'Perfecto 👌 Al momento de pedir vas a poder ver el tiempo estimado según tu dirección antes de confirmar.';
    return {
      text,
      products: null,
      fallbackKeys: ['order_hint', 'catalog'],
      containmentIncluded: true,
      frustrationLevel: 0,
    };
  }

  const cLvl = conversationalComplaintLevel(n);
  if (cLvl > 0) {
    return {
      text: complaintBodyText(cLvl, false),
      products: null,
      fallbackKeys: complaintFallbackKeys(business, cLvl),
      containmentIncluded: true,
      frustrationLevel: 0,
    };
  }

  return null;
}

export function frustrationPrefix(level) {
  if (level <= 0) return '';
  const strong = [
    'Entiendo tu molestia y quiero ayudarte 🙌',
    'Pucha, lamento que te haya pasado eso 😕',
    'Quiero ayudarte a resolverlo lo antes posible.',
  ];
  const mild = ['Entiendo, gracias por contarlo.', 'Quiero ayudarte con eso.'];
  const pool = level >= 2 ? strong : mild;
  return pool[level % pool.length];
}

export function humanFallbackCopy() {
  const picks = [
    'Te ayudo altiro 👌',
    'Entiendo, déjame ayudarte con eso.',
    'Puedo ayudarte a pedir, ver promos o resolver una duda 👇',
  ];
  return picks[Math.floor(Math.random() * picks.length)];
}